import { DesignResource, useGetProjectsQuery, usePostDesignsByDesignDuplicateMutation } from '@/api/serverApi'
import { Button, InputLabel, LoadingSpinner, SearchableSelect, TextInput, useErrorHandler } from '@cfs-fixings/shared'
import { SelectOption } from '@cfs-fixings/shared/src/components/dropdown-input/MinimalSelect'
import toastHelper from '@cfs-fixings/shared/src/helpers/toastHelper'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import * as v from 'valibot'

const duplicateDesignSchema = v.object({
  project_id: v.nonOptional(v.string([v.minLength(1, 'A project is required')]), 'A project is required'),
  name: v.string([v.minLength(1, 'Name is required')]),
})

type DuplicateDesignFormInputs = v.Input<typeof duplicateDesignSchema>

type PropTypes = {
  design: DesignResource,
  onCompleted?: (project: DesignResource) => void,
  onClose?: () => void,
}

function DuplicateDesignForm ({ design, onCompleted, onClose }: PropTypes) {
  const textSpec = {
    projectField: {
      label: 'Project',
      placeholder: 'Choose a project',
    },
    designNameField: {
      label: 'Name',
      placeholder: 'Enter name',
    },
    buttonLabels: {
      cancel: 'Cancel',
      create: 'Duplicate Design',
    },
    successToastMessage: {
      title: 'Design duplicated',
      body: 'Your duplicate design has been created successfully.',
    },
    errorToastMessage: {
      title: 'Something went wrong',
      body: 'Please try again later.',
    },
  }

  const errorHandler = useErrorHandler()

  const { register, handleSubmit, formState: { errors }, setError, reset, control } = useForm<DuplicateDesignFormInputs>({
    resolver: valibotResolver(duplicateDesignSchema),
  })

  const [customerSearchQuery, setCustomerSearchQuery] = useState('')

  const [duplicate, { isLoading: isSubmitting }] = usePostDesignsByDesignDuplicateMutation()

  const { data: response } = useGetProjectsQuery({
    search: customerSearchQuery,
    perPage: 5,
  })

  const onSubmit: SubmitHandler<DuplicateDesignFormInputs> = (data) => {
    duplicate({
      design: design.id,
      body: data,
    })
      .unwrap()
      .then((response) => {
        onCompleted?.(response.data)
        toastHelper.success(textSpec.successToastMessage.title, textSpec.successToastMessage.body)
        reset()
      })
      .catch((error) => {
        if (error?.status === 422) {
          errorHandler(error, setError)
        } else {
          toastHelper.error(textSpec.errorToastMessage.title, error.data?.message ?? textSpec.errorToastMessage.body)
        }
      })
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col items-start w-full max-w-[615px]"
    >
      {
        response?.data && <div className="my-[17px] w-full flex flex-col gap-[30px]">
          {/* Customer Picker */}
          <div>
            <InputLabel>{textSpec.projectField.label}</InputLabel>
            <Controller
              control={control}
              name={'project_id'}
              render={({ field }) => {
                const handleOnChange = (e: SelectOption) => field.onChange(e.value)
                return (
                  <SearchableSelect
                    placeholder={textSpec.projectField.placeholder}
                    defaultValue={field.value}
                    options={response?.data.map((project) => ({ label: project.name, value: project.id })) ?? []}
                    onChange={(option) => handleOnChange(option)}
                    onSearchChange={(value) => setCustomerSearchQuery(value)}
                    error={errors?.project_id?.message as string}
                  />
                )
              }}
            />
          </div>

          {/* Name Field */}
          <div>
            <InputLabel>{textSpec.designNameField.label}</InputLabel>
            <TextInput
              placeholder={textSpec.designNameField.placeholder}
              error={errors?.name?.message}
              {...register('name')}
            />
          </div>
        </div>
      }

      {
        !response?.data && <div className="w-full flex items-center justify-center my-8">
          <LoadingSpinner className="w-4 h-4" />
        </div>
      }

      <div className="w-full flex flex-row justify-end gap-2.5 mt-4">
        <Button
          key="close"
          variant="secondary"
          isLoading={isSubmitting}
          onClick={onClose}
        >
          {textSpec.buttonLabels.cancel}
        </Button>
        <Button
          key="create"
          variant="dark"
          isLoading={isSubmitting}
          type="submit"
        >
          {textSpec.buttonLabels.create}
        </Button>
      </div>
    </form>
  )
}

export default DuplicateDesignForm
